<template>
  <el-card class="reagent-mall-prestore container" shadow="never">
    <div slot="header" class="card-header">
      <div class="desciconLarge"></div>
      <span class="font18">申请预存</span>
    </div>

    <div v-loading="pageLoading" class="page-body">
      <el-form ref="form" :model="form" :rules="formRules" label-width="130px" class="form">
        <section class="form-section">
          <el-form-item label="预存类型">
            <el-radio-group v-model="form.prestoredType">
              <el-radio border :label="1">个人预存</el-radio>
              <el-radio border :label="2" :disabled="!hasReagentTeam">团队预存</el-radio>
            </el-radio-group>
            <span v-if="form.prestoredType === 2" class="team-name">预存团队：{{ teamName }}</span>
            <span v-else-if="!hasReagentTeam" class="form-tip">暂未获取到商城团队信息，当前仅可申请个人预存</span>
          </el-form-item>
          <el-form-item label="预存有礼">
            <el-checkbox v-model="form.prestoreGiftType" :true-label="1" :false-label="0" border>积分</el-checkbox>
          </el-form-item>
        </section>

        <section class="form-section">
          <el-form-item prop="invoiceItems">
            <template slot="label"><span class="require-icon">*</span>开票信息</template>
            <div class="invoice-box-list">
              <div class="invoice-box" v-for="(invoice, invoiceIndex) in form.invoices" :key="invoice.rowKey">
                <div class="box-title">
                  <div class="invoice-title-row">
                    <span>发票{{ invoiceIndex + 1 }}</span>
                    <el-date-picker v-model="invoice.clientNeedInvoiceDate" value-format="yyyy-MM-dd"
                      type="date" placeholder="选择日期" :picker-options="pickerOptions"></el-date-picker>
                  </div>
                  <div class="invoice-date">
                    <i v-if="form.invoices.length > 1" class="el-icon-delete-solid" @click="removeInvoice(invoiceIndex)"></i>
                    <i class="el-icon-circle-plus" @click="addInvoice"></i>
                  </div>
                </div>
                <table>
                  <thead>
                    <tr>
                      <th class="sku-col">商品 SKU</th>
                      <th>项目名称</th>
                      <th>单价</th>
                      <th>数量</th>
                      <th>计量单位</th>
                      <th>小计（元）</th>
                      <th class="operation-col">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, itemIndex) in invoice.items" :key="item.rowKey">
                      <td>
                        <el-select v-model="item.skuId" filterable remote reserve-keyword clearable
                          :remote-method="searchSkuOptions" :loading="skuLoading" class="sku-select"
                          placeholder="请输入商品名称或 SKU" @change="handleSkuChange(invoiceIndex, itemIndex, $event)">
                          <el-option v-for="option in skuOptions" :key="option.skuId" :value="option.skuId"
                            :label="option.goodsName" :disabled="isSkuSelectedByOtherRow(invoiceIndex, option.skuId, itemIndex)"></el-option>
                        </el-select>
                      </td>
                      <td><el-input v-model.trim="item.invoiceContentName" placeholder="请输入项目名称"></el-input></td>
                      <td>
                        <el-input-number v-model="item.unitPrice" :min="0" :max="99999999" :precision="2"
                          controls-position="right" class="number-input" @change="calculateItemSubtotal(item)"></el-input-number>
                      </td>
                      <td>
                        <el-input-number v-model="item.quantity" :min="0.01" :max="999999" :precision="2"
                          controls-position="right" class="number-input" @change="calculateItemSubtotal(item)"></el-input-number>
                      </td>
                      <td>{{ item.unit || '-' }}</td>
                      <td class="subtotal">{{ formatMoney(item.subtotal) }}</td>
                      <td>
                        <el-button type="text" class="delete-btn" @click="removeItem(invoiceIndex, itemIndex)">
                          <i class="el-icon-remove"></i>
                        </el-button>
                      </td>
                    </tr>
                    <tr class="invoice-summary-row">
                      <td>
                        <div class="invoice-total">合计：￥{{ formatMoney(getInvoiceTotal(invoice)) }}</div>
                      </td>
                      <td colspan="6">
                        <div class="add-project" @click="addItem(invoiceIndex)">
                          <i class="el-icon-circle-plus-outline"></i>
                          <span>新增商品 SKU</span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </el-form-item>
        </section>

        <section class="form-section">
          <el-form-item label="申请备注">
            <el-input v-model.trim="form.remark" type="textarea" :rows="3" :maxlength="200" show-word-limit
              placeholder="请填写备注"></el-input>
          </el-form-item>
          <el-form-item label="发票类型">
            <el-radio-group v-model="form.invoiceType" @change="handleInvoiceTypeChange">
              <el-radio border :label="1">普通发票</el-radio>
              <el-radio border :label="2">专用发票</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="发票抬头">
            <el-radio-group v-model="form.ifDefault" @change="handleInvoiceHeadModeChange">
              <el-radio border :label="1">使用默认抬头</el-radio>
              <el-radio border :label="0">填写本次抬头</el-radio>
            </el-radio-group>
          </el-form-item>
          <div class="invoice-fields">
            <el-form-item label="发票抬头" prop="invoiceTitle">
              <el-select v-model="form.invoiceTitle" filterable remote reserve-keyword allow-create
                default-first-option clearable :remote-method="searchInvoiceHeads" :loading="invoiceHeadLoading"
                placeholder="请输入发票抬头搜索" class="invoice-head-select" @change="selectInvoiceHead">
                <el-option v-for="head in invoiceList" :key="head.invoiceHeadId || head.id || head.invoiceTitle"
                  :label="head.invoiceTitle" :value="head.invoiceTitle"></el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="企业税号" prop="enterpriseTaxNumber">
              <el-input v-model.trim="form.enterpriseTaxNumber" maxlength="30" placeholder="请输入企业税号"></el-input>
            </el-form-item>
            <el-form-item label="开户行名称" prop="openBankName">
              <template slot="label">开户行名称</template>
              <el-input v-model.trim="form.openBankName" maxlength="100" placeholder="请输入开户行名称"></el-input>
            </el-form-item>
            <el-form-item label="开户行账号" prop="openBankAccount">
              <template slot="label">开户行账号</template>
              <el-input v-model.trim="form.openBankAccount" maxlength="30" placeholder="请输入开户行账号"></el-input>
            </el-form-item>
            <el-form-item label="注册地址" prop="registeredAddress">
              <template slot="label">注册地址</template>
              <el-input v-model.trim="form.registeredAddress" maxlength="200" placeholder="请输入注册地址"></el-input>
            </el-form-item>
            <el-form-item label="注册电话" prop="registeredTelephone">
              <template slot="label">注册电话</template>
              <el-input v-model.trim="form.registeredTelephone" maxlength="30" placeholder="请输入注册电话"></el-input>
            </el-form-item>
            <el-form-item label="收件人姓名" prop="addressee">
              <template slot="label">收件人姓名</template>
              <el-input v-model.trim="form.addressee" maxlength="50" placeholder="请输入收件人姓名"></el-input>
            </el-form-item>
            <el-form-item label="收件地址" prop="deliveryAddress">
              <template slot="label">收件地址</template>
              <el-input v-model.trim="form.deliveryAddress" maxlength="200" placeholder="请输入收件地址"></el-input>
            </el-form-item>
            <el-form-item label="收件联系人" prop="receivingContact">
              <template slot="label">收件联系人</template>
              <el-input v-model.trim="form.receivingContact" maxlength="50" placeholder="请输入邮箱"></el-input>
            </el-form-item>
            <el-form-item label="收件联系方式" prop="receivingContactInformation">
              <el-input v-model.trim="form.receivingContactInformation" maxlength="80" placeholder="请输入收件联系方式"></el-input>
            </el-form-item>
          </div>
        </section>

        <div class="footer-actions">
          <el-button @click="goBack">返回</el-button>
          <el-button type="primary" :loading="submitting" @click="handleSubmit">提交申请</el-button>
        </div>
      </el-form>
    </div>
  </el-card>
</template>

<script>
import { getClient, getReagentTeamInfo } from '@/plugins/auth'
import { validEnterpriseTax, validSpace } from '@/plugins/validate.js'
import { applyReagentMallFinance, getInvoiceHeadContrastList, listInvoiceHead, reagentMallPrestoreSkuOptions } from '@/api/request'

function parseStorageObject(getter) {
  try {
    return JSON.parse(getter() || '{}')
  } catch (error) {
    return {}
  }
}

function createItem() {
  return {
    rowKey: Date.now() + '-' + Math.random().toString(16).slice(2),
    goodsId: '',
    skuId: '',
    goodsName: '',
    skuNo: '',
    packageSpec: '',
    invoiceContentName: '',
    unit: '',
    quantity: 1,
    unitPrice: 0,
    subtotal: 0
  }
}

function createInvoice() {
  return {
    rowKey: Date.now() + '-' + Math.random().toString(16).slice(2),
    clientNeedInvoiceDate: '',
    items: [createItem()]
  }
}

function roundMoney(value) {
  const amount = Number(value)
  return Number.isFinite(amount) ? Math.round((amount + Number.EPSILON) * 100) / 100 : 0
}

export default {
  name: 'ReagentMallApplicationPre',
  data() {
    return {
      pageLoading: false,
      submitting: false,
      skuLoading: false,
      invoiceHeadLoading: false,
      clientInfo: parseStorageObject(getClient),
      reagentTeamInfo: parseStorageObject(getReagentTeamInfo),
      skuOptions: [],
      defaultInvoiceHead: {},
      invoiceList: [],
      form: {
        // 后续订单开票可复用该统一申请接口，并切换为 applyType: 1。
        applyType: 2,
        prestoreId: 0,
        prestoredType: 1,
        // 预存有礼仅支持积分：选中为 1，未选中为 0。
        prestoreGiftType: 1,
        amountDeposited: 0,
        invoiceType: 1,
        ifDefault: 0,
        backAutoInvoiceFlag: 1,
        remark: '',
        invoiceTitle: '',
        enterpriseTaxNumber: '',
        openBankName: '',
        openBankAccount: '',
        registeredAddress: '',
        registeredTelephone: '',
        addressee: '',
        deliveryAddress: '',
        receivingContact: '',
        receivingContactInformation: '',
        invoices: [createInvoice()]
      },
      pickerOptions: {
        disabledDate(time) {
          return time.getTime() < Date.now() - 24 * 60 * 60 * 1000
        }
      }
    }
  },
  computed: {
    hasReagentTeam() {
      return Boolean(this.reagentTeamInfo.rmTeamId)
    },
    teamName() {
      return this.reagentTeamInfo.teamName || this.reagentTeamInfo.rmTeamName || this.clientInfo.teamName || '当前团队'
    },
    hasDefaultInvoiceHead() {
      return Boolean(this.defaultInvoiceHead.invoiceTitle)
    },
    totalAmount() {
      return roundMoney(this.form.invoices.reduce((total, invoice) => total + this.getInvoiceTotal(invoice), 0))
    },
    totalAmountText() {
      return this.formatMoney(this.totalAmount)
    },
    formRules() {
      const specialInvoice = this.form.invoiceType === 2
      const invoiceTitleValidator = (rule, value, callback) => {
        const title = String(value || '').trim()
        if (validSpace(title)) {
          callback(new Error('发票抬头不能包含空格或换行'))
          return
        }
        callback()
      }
      const enterpriseTaxNumberValidator = (rule, value, callback) => {
        const taxNumber = String(value || '').trim()
        if (!taxNumber) {
          callback(new Error('请输入企业税号'))
          return
        }
        if (validSpace(taxNumber)) {
          callback(new Error('企业税号不能包含空格或换行'))
          return
        }
        if (!validEnterpriseTax(taxNumber)) {
          callback(new Error('请输入正确的企业税号'))
          return
        }
        callback()
      }
      return {
        // 发票抬头和企业税号在普通发票、专用发票下都必填。
        invoiceTitle: [
          { required: true, message: '请输入发票抬头', trigger: 'blur' },
          { validator: invoiceTitleValidator, trigger: 'blur' }
        ],
        enterpriseTaxNumber: [
          { required: true, message: '请输入企业税号', trigger: 'blur' },
          { validator: enterpriseTaxNumberValidator, trigger: 'blur' }
        ],
        // 默认抬头只负责回填；专票附加字段是否必填仅由发票类型决定。
        openBankName: specialInvoice ? [{ required: true, message: '请输入开户行名称', trigger: 'blur' }] : [],
        openBankAccount: specialInvoice ? [{ required: true, message: '请输入开户行账号', trigger: 'blur' }] : [],
        registeredAddress: specialInvoice ? [{ required: true, message: '请输入注册地址', trigger: 'blur' }] : [],
        registeredTelephone: specialInvoice ? [{ required: true, message: '请输入注册电话', trigger: 'blur' }] : [],
        addressee: specialInvoice ? [{ required: true, message: '请输入收件人姓名', trigger: 'blur' }] : [],
        deliveryAddress: specialInvoice ? [{ required: true, message: '请输入收件地址', trigger: 'blur' }] : [],
        receivingContact: specialInvoice ? [{ required: true, message: '请输入收件联系人', trigger: 'blur' }] : [],
        receivingContactInformation: specialInvoice ? [{ required: true, message: '请输入收件联系方式', trigger: 'blur' }] : []
      }
    }
  },
  watch: {
    totalAmount(value) {
      this.form.amountDeposited = value
    }
  },
  created() {
    this.loadDefaultInvoiceHead()
    this.searchSkuOptions('')
  },
  methods: {
    async loadDefaultInvoiceHead() {
      if (!this.clientInfo.clientId) return
      this.pageLoading = true
      try {
        const response = await listInvoiceHead({ clientId: this.clientInfo.clientId })
        const rows = Array.isArray(response.rows) ? response.rows : []
        this.invoiceList = rows
        this.defaultInvoiceHead = rows.find(item => Number(item.isDefault) === 1) || {}
        if (this.hasDefaultInvoiceHead) {
          this.form.ifDefault = 1
          this.applyInvoiceHead(this.defaultInvoiceHead)
        }
      } catch (error) {
        this.invoiceList = []
        this.defaultInvoiceHead = {}
      } finally {
        this.pageLoading = false
      }
    },
    async searchSkuOptions(keyword) {
      this.skuLoading = true
      try {
        const response = await reagentMallPrestoreSkuOptions({
          keyword: String(keyword || '').trim(),
          pageNum: 1,
          pageSize: 50
        })
        this.skuOptions = Array.isArray(response.rows) ? response.rows : []
      } catch (error) {
        this.skuOptions = []
      } finally {
        this.skuLoading = false
      }
    },
    async searchInvoiceHeads(keyword) {
      const invoiceTitle = String(keyword || '').trim()
      if (!invoiceTitle) {
        this.invoiceList = []
        return
      }
      this.invoiceHeadLoading = true
      try {
        const response = await getInvoiceHeadContrastList({ invoiceTitle })
        this.invoiceList = Array.isArray(response.rows) ? response.rows : []
      } catch (error) {
        this.invoiceList = []
      } finally {
        this.invoiceHeadLoading = false
      }
    },
    formatSkuLabel(option) {
      return [option.goodsName, option.skuNo, option.packageSpec].filter(Boolean).join(' · ')
    },
    isSkuSelectedByOtherRow(invoiceIndex, skuId, currentIndex) {
      const invoice = this.form.invoices[invoiceIndex]
      return invoice.items.some((item, index) => index !== currentIndex && String(item.skuId) === String(skuId))
    },
    handleSkuChange(invoiceIndex, itemIndex, skuId) {
      const item = this.form.invoices[invoiceIndex].items[itemIndex]
      const sku = this.skuOptions.find(option => String(option.skuId) === String(skuId))
      if (!sku) {
        Object.assign(item, createItem(), { rowKey: item.rowKey })
        return
      }
      if (this.isSkuSelectedByOtherRow(invoiceIndex, sku.skuId, itemIndex)) {
        this.$message.warning('同一张发票中不能重复选择 SKU')
        Object.assign(item, createItem(), { rowKey: item.rowKey })
        return
      }
      // 下拉接口字段按申请接口 items 字段约定进行映射。
      Object.assign(item, {
        goodsId: sku.goodsId,
        skuId: sku.skuId,
        goodsName: sku.goodsName || '',
        skuNo: sku.skuNo || '',
        packageSpec: sku.packageSpec || '',
        invoiceContentName: sku.invoiceContentName || '',
        unit: sku.unit || '',
        quantity: item.quantity > 0 ? item.quantity : 1,
        unitPrice: roundMoney(sku.price)
      })
      this.calculateItemSubtotal(item)
    },
    calculateItemSubtotal(item) {
      item.subtotal = roundMoney(Number(item.quantity || 0) * Number(item.unitPrice || 0))
    },
    getInvoiceTotal(invoice) {
      return roundMoney(invoice.items.reduce((total, item) => total + Number(item.subtotal || 0), 0))
    },
    addInvoice() {
      this.form.invoices.push(createInvoice())
    },
    removeInvoice(index) {
      if (this.form.invoices.length === 1) {
        this.$message.warning('至少保留一张发票')
        return
      }
      this.form.invoices.splice(index, 1)
    },
    addItem(invoiceIndex) {
      this.form.invoices[invoiceIndex].items.push(createItem())
    },
    removeItem(invoiceIndex, itemIndex) {
      const items = this.form.invoices[invoiceIndex].items
      if (items.length === 1) {
        const rowKey = items[0].rowKey
        Object.assign(items[0], createItem(), { rowKey })
        return
      }
      items.splice(itemIndex, 1)
    },
    formatMoney(value) {
      return roundMoney(value).toFixed(2)
    },
    applyInvoiceHead(head) {
      if (!head) return
      Object.assign(this.form, {
        invoiceTitle: head.invoiceTitle || '',
        enterpriseTaxNumber: head.enterpriseTaxNumber || '',
        openBankName: head.openBankName || '',
        openBankAccount: head.openBankAccount || '',
        registeredAddress: head.registeredAddress || '',
        registeredTelephone: head.registeredTelephone || '',
        addressee: head.addressee || '',
        deliveryAddress: head.deliveryAddress || '',
        receivingContact: head.receivingContact || '',
        receivingContactInformation: head.receivingContactInformation || ''
      })
    },
    selectInvoiceHead(value) {
      const head = this.invoiceList.find(item => item.invoiceTitle === value)
      if (head) this.applyInvoiceHead(head)
      this.clearInvoiceValidation()
    },
    clearInvoiceHeadFields() {
      Object.assign(this.form, {
        invoiceTitle: '',
        enterpriseTaxNumber: '',
        openBankName: '',
        openBankAccount: '',
        registeredAddress: '',
        registeredTelephone: '',
        addressee: '',
        deliveryAddress: '',
        receivingContact: '',
        receivingContactInformation: ''
      })
    },
    handleInvoiceHeadModeChange(value) {
      if (value === 1) {
        if (!this.hasDefaultInvoiceHead) {
          this.$confirm('您还未添加过发票抬头，是否前往添加？', '温馨提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }).then(() => {
            this.$router.push({
              path: '/aboutUsIndex',
              query: {
                tabIndex: '3-3'
              }
            })
          }).catch(() => {
            this.form.ifDefault = 0
          })
        } else {
          this.applyInvoiceHead(this.defaultInvoiceHead)
          this.clearInvoiceValidation()
        }
      } else {
        this.clearInvoiceHeadFields()
        this.clearInvoiceValidation()
      }
    },
    handleInvoiceTypeChange() {
      this.clearInvoiceValidation()
    },
    clearInvoiceValidation() {
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.clearValidate()
      })
    },
    validateItems() {
      const invalidInvoice = this.form.invoices.find(invoice => {
        return !invoice.items.length ||
          invoice.items.some(item => !item.skuId || Number(item.quantity) <= 0 || Number(item.unitPrice) < 0) ||
          this.getInvoiceTotal(invoice) <= 0
      })
      if (invalidInvoice) {
        this.$message.warning('请完整填写每张发票的商品 SKU、项目名称、数量和单价')
        return false
      }
      if (this.totalAmount <= 0) {
        this.$message.warning('申请预存金额必须大于 0')
        return false
      }
      if (this.form.prestoredType === 2 && !this.hasReagentTeam) {
        this.$message.warning('未获取到商城团队信息，无法申请团队预存')
        return false
      }
      return true
    },
    buildPayload() {
      const payload = {
        applyType: 2,
        prestoreId: 0,
        prestoredType: this.form.prestoredType,
        prestoreGiftType: this.form.prestoreGiftType,
        amountDeposited: this.totalAmount,
        invoiceType: this.form.invoiceType,
        ifDefault: this.form.ifDefault,
        backAutoInvoiceFlag: 1,
        remark: this.form.remark || undefined,
        invoices: this.form.invoices.map(invoice => ({
          invoiceAmount: this.getInvoiceTotal(invoice),
          clientNeedInvoiceDate: invoice.clientNeedInvoiceDate || undefined,
          items: invoice.items.map(item => ({
            goodsId: item.goodsId,
            skuId: item.skuId,
            goodsName: item.goodsName,
            skuNo: item.skuNo,
            packageSpec: item.packageSpec,
            invoiceContentName: item.invoiceContentName,
            unit: item.unit,
            quantity: Number(item.quantity),
            unitPrice: roundMoney(item.unitPrice),
            subtotal: roundMoney(item.subtotal)
          }))
        }))
      }
      if (this.form.prestoredType === 2) payload.rmTeamId = this.reagentTeamInfo.rmTeamId
      else if (this.clientInfo.clientId) payload.rmClientId = this.clientInfo.clientId

      if (this.form.ifDefault === 0) {
        Object.assign(payload, {
          invoiceTitle: this.form.invoiceTitle,
          enterpriseTaxNumber: this.form.enterpriseTaxNumber,
          openBankName: this.form.openBankName || undefined,
          openBankAccount: this.form.openBankAccount || undefined,
          registeredAddress: this.form.registeredAddress || undefined,
          registeredTelephone: this.form.registeredTelephone || undefined,
          addressee: this.form.addressee || undefined,
          deliveryAddress: this.form.deliveryAddress || undefined,
          receivingContact: this.form.receivingContact || undefined,
          receivingContactInformation: this.form.receivingContactInformation || undefined
        })
      }
      return payload
    },
    handleSubmit() {
      if (!this.$refs.form || !this.validateItems()) return
      this.$refs.form.validate(async valid => {
        if (!valid) return
        this.submitting = true
        try {
          await applyReagentMallFinance(this.buildPayload())
          this.$message.success('预存申请已提交，请等待审核')
          this.$emit('backAndRefresh')
        } finally {
          this.submitting = false
        }
      })
    },
    goBack() {
      this.$emit('goBack')
    }
  }
}
</script>

<style lang="scss" scoped>
.container {
  width: 100%;
}

.require-icon,
table th i {
  color: #ff4a2b;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-body {
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  height: calc(100vh - 300px);
  box-sizing: border-box;
}

.form {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  padding: 0 50px;
  box-sizing: border-box;
}

.form-section {
  max-width: 100%;
  min-width: 0;
  margin-bottom: 15px;
}

.form-section h3 {
  margin: 0 0 15px;
  color: #303133;
  font-size: 18px;
  font-weight: 600;
}

.section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
}

.section-heading h3 {
  margin: 0;
}

.section-heading span,
.form-tip {
  color: #5d5d5d;
  font-size: 14px;
}

.team-name {
  margin-left: 16px;
  color: #606266;
}

.invoice-box {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  margin-bottom: 15px;
  padding: 15px;
  border: 1px solid #ddd;
  border-radius: 10px;
  box-sizing: border-box;
}

.box-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  font-size: 18px;
  font-weight: 600;
}

.invoice-title-row,
.invoice-date {
  display: flex;
  align-items: center;
  column-gap: 15px;
}

.invoice-date i {
  cursor: pointer;
  font-size: 30px;
}

.invoice-date .el-icon-delete-solid {
  color: #ff4a2b;
}

.invoice-date .el-icon-circle-plus {
  color: #4d6fff;
}

.table-wrap {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
}

table {
  width: 100%;
  max-width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  margin: 10px 0;
}

th,
td {
  padding: 8px;
  text-align: center;
  border: 1px solid #ddd;
  word-break: normal;
  overflow-wrap: break-word;
}

th {
  color: #303133;
  background-color: #f2f2f2;
  font-weight: 600;
}

tr:last-child {
  background-color: #f2f2f2;
}

tr:hover {
  background-color: #84b7f930;
}

.invoice-summary-row td:first-child {
  width: 40%;
}

.invoice-summary-row:hover {
  background-color: #f2f2f2;
}

.invoice-total {
  color: #ff4a2b;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
}

.add-project {
  display: flex;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  column-gap: 10px;
  color: #5d5d5d;
  font-size: 16px;
}

.add-project:hover {
  color: #84b7f9;
}

.add-project i {
  font-size: 16px;
}

.sku-col {
  width: 275px;
  min-width: 275px;
}

.invoice-box table th:nth-child(2),
.invoice-box table td:nth-child(2) {
  width: 18%;
}

.invoice-box table th:nth-child(3),
.invoice-box table td:nth-child(3) {
  width: 14%;
}

.invoice-box table th:nth-child(4),
.invoice-box table td:nth-child(4) {
  width: 14%;
}

.invoice-box table th:nth-child(5),
.invoice-box table td:nth-child(5) {
  width: 10%;
}

.invoice-box table th:nth-child(6),
.invoice-box table td:nth-child(6) {
  width: 12%;
}

.operation-col {
  width: 60px;
  min-width: 60px;
}

.sku-select,
.number-input,
.invoice-head-select {
  display: block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.subtotal {
  color: #ff4a2b;
  font-weight: 600;
}

.delete-btn {
  cursor: pointer;
  padding: 0;
  color: #ff4a2b;
  font-size: 24px;
}

.delete-btn ::v-deep i {
  font-size: 24px;
}

.add-item {
  margin-top: 0;
  color: #5d5d5d;
  border-color: #dcdfe6;
}

.amount-summary {
  display: flex;
  justify-content: flex-end;
  align-items: baseline;
  gap: 16px;
  margin-top: 10px;
  color: #5d5d5d;
  font-size: 18px;
  font-weight: 600;
}

.amount-summary strong {
  color: #ff4a2b;
}

.invoice-fields {
  display: flex;
  flex-wrap: wrap;
  row-gap: 30px;
  column-gap: 10%;
  max-width: 100%;
}

.invoice-fields .el-form-item {
  width: 40%;
  margin-bottom: 0;
}

.footer-actions {
  display: flex;
  justify-content: space-around;
  width: 50%;
  margin: 15px auto 0;
}

.footer-actions .el-button {
  width: 150px;
}

@media (max-width: 768px) {
  .page-body {
    height: calc(100vh - 220px);
  }

  .form {
    padding: 0 15px;
  }

  .form-section {
    padding: 0;
  }

  .invoice-box {
    padding: 10px;
  }

  .invoice-title-row {
    min-width: 0;
    flex-wrap: wrap;
  }

  .invoice-title-row .el-date-editor {
    max-width: 100%;
  }

  .invoice-fields {
    display: block;
  }

  .invoice-fields .el-form-item {
    width: 100%;
  }

  .footer-actions {
    width: 100%;
    gap: 16px;
  }
}
</style>
